import { useCallback, useMemo } from 'react';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { includes } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AccessControl from '@gef-ui/cdu-user/components/AccessControl';
import { getLabelMessage } from '@gef-ui/localization/messages';
import FileExport from '@gef-ui/material-icons/FileExport';
import Print from '@gef-ui/material-icons/Print';

import { ComponentLoader, Dialog } from 'components';
import Panel from 'components/atoms/GefMui/Panel';
import Subheader from 'components/atoms/GefMui/Subheader';
import { EMPTY_VALUES } from 'constants/general';
import MODALS from 'constants/modals';
import PAGES from 'constants/pages';
import { KCL_ACCESS_TO_KANCELAR } from 'features/common/acl/constants';
import fetch from 'features/common/fetch';
import { useRenderSubheaderTitle } from 'features/common/Subheader/hooks';
import campaigns from 'features/views/campaignDashboards/campaigns';
import dashboardSidebar from 'features/views/campaignDashboards/sidebar';
import { PRINT_AVAILABLE_VIEWS } from 'features/views/campaignDashboards/sidebar/constants';
import dashboardStructure from 'features/views/campaignDashboards/structure';
import contactFeedback from 'features/views/contactFeedback';
import contacts from 'features/views/contacts';
import { getRoutePath } from 'utils/routeHelper';
import BruttoProductionModal from 'pages/BruttoProductionModal';

import DashboardCentral from '../DashboardCentral';
import DashboardManager from '../DashboardManager';
import DashboardPpz from '../DashboardPpz';
import DashboardRegion from '../DashboardRegion';
import DashboardSeller from '../DashboardSeller';
import CampaignOverview from './components/CampaignOverview';

const { views } = dashboardStructure.constants;

export const CampaignDashboards = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const view = useSelector(dashboardStructure.selectors.getView);
	const isOverviewOrUnknown =
		view === views.OVERVIEW ||
		!includes(view, [views.CENTRAL, views.REGION, views.MANAGER, views.SELLER, views.PPZ]);

	const campaignId = useSelector(dashboardStructure.selectors.getCampaignId);
	const campaign = useSelector((state) => campaigns.selectors.getCampaignById(state, campaignId));
	const contactsAvailable = useSelector(contacts.selectors.getContactIdsByDashboardsView);

	const generateScreenInfo = useCallback(() => {
		const result = {};
		switch (view) {
			case views.CENTRAL:
				result.screenId = PAGES.campaignDashboards.central.id;
				result.screenName = PAGES.campaignDashboards.central.name;
				result.screenTitle = getLabelMessage('dashboardCentral:title');
				break;
			case views.REGION:
				result.screenId = PAGES.campaignDashboards.region.id;
				result.screenName = PAGES.campaignDashboards.region.name;
				result.screenTitle = getLabelMessage('dashboardRegion:title');
				break;
			case views.MANAGER:
				result.screenId = PAGES.campaignDashboards.manager.id;
				result.screenName = PAGES.campaignDashboards.manager.name;
				result.screenTitle = getLabelMessage('dashboardManager:title');
				break;
			case views.SELLER:
				result.screenId = PAGES.campaignDashboards.seller.id;
				result.screenName = PAGES.campaignDashboards.seller.name;
				result.screenTitle = getLabelMessage('dashboardSeller:title');
				break;
			case views.PPZ:
				result.screenId = PAGES.campaignDashboards.ppz.id;
				result.screenName = PAGES.campaignDashboards.ppz.name;
				result.screenTitle = getLabelMessage('dashboardPpz:title');
				break;
		}

		return result;
	}, [view]);

	const { screenId, screenName, screenTitle } = generateScreenInfo();

	const handleGoBack = useCallback(() => {
		dispatch(dashboardStructure.actions.returnToPreviousView());
	}, [dispatch]);

	const subheaderTitle = useRenderSubheaderTitle({
		pageTitle: `${screenTitle} (${campaign?.fullName})`,
		backButton: true,
		onBack: handleGoBack,
	});

	const convertView = useCallback((view) => {
		switch (view) {
			case views.MANAGER:
				return 'dashboard-manager';
			case views.SELLER:
				return 'dashboard-seller';
			case views.PPZ:
				return 'dashboard-ppz';
			default:
				return null;
		}
	}, []);

	const handlePrintButton = useCallback(() => {
		const { pathname, state } = getRoutePath('printContactsList', EMPTY_VALUES.OBJECT, true);
		state.view = convertView(view);
		navigate(pathname, { state });
	}, [navigate, convertView, view]);

	const handleCampaignReport = useCallback(() => {
		dispatch(dashboardSidebar.actions.reportCampaign(campaignId));
	}, [dispatch, campaignId]);

	const subheaderToolbar = useMemo(() => {
		return (
			<Stack
				direction="row"
				spacing={1}
				sx={{
					justifyContent: 'flex-end',
				}}
			>
				<ComponentLoader
					fetchStorePath={fetch.constants.urls.dashboardReport}
					onReload={handleCampaignReport}
					ignoreErrors
				>
					<Button variant="text" size="small" startIcon={<FileExport />} onClick={handleCampaignReport}>
						{getLabelMessage('campaigns:report')}
					</Button>
				</ComponentLoader>
				{includes(view, PRINT_AVAILABLE_VIEWS) && (
					<Button
						variant="text"
						size="small"
						startIcon={<Print />}
						onClick={handlePrintButton}
						disabled={isNilOrEmpty(contactsAvailable)}
					>
						{getLabelMessage('common:print')}
					</Button>
				)}
			</Stack>
		);
	}, [view, contactsAvailable, handlePrintButton, handleCampaignReport]);

	const floatingDialogs = (
		<>
			<Dialog mui name={contactFeedback.constants.dialogs.PARTNER_LOCKED_DIALOG}>
				{getLabelMessage('contactDetail:partnerLocked')}
			</Dialog>
			<Dialog mui name={dashboardSidebar.constants.REPORT_NOT_FOUND_DIALOG}>
				{getLabelMessage('campaigns:reportNotFound')}
			</Dialog>
		</>
	);

	const createView = useCallback((view) => {
		switch (view) {
			case views.PPZ:
				return <DashboardPpz />;
			case views.SELLER:
				return <DashboardSeller />;
			case views.MANAGER:
				return <DashboardManager />;
			case views.REGION:
				return <DashboardRegion />;
			case views.CENTRAL:
				return <DashboardCentral />;
			case views.OVERVIEW:
		}
	}, []);

	return isOverviewOrUnknown ? (
		<CampaignOverview />
	) : (
		<Panel screenId={screenId} screenName={screenName} floatingDialogs={floatingDialogs}>
			<BruttoProductionModal id={MODALS.brutto_production} />
			<Paper sx={{ p: 3, borderRadius: 2 }}>
				<Subheader title={subheaderTitle} headerToolbar={subheaderToolbar} />
			</Paper>
			<AccessControl accessLevel={KCL_ACCESS_TO_KANCELAR}>{createView(view)}</AccessControl>
		</Panel>
	);
};

export default CampaignDashboards;
